import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { empresaService, usuarioService, departamentoService, okrService, keyResultService, auditoriaEventoService, AuditoriaEventoEntry } from '@/services/api';
import { Empresa, Usuario, Departamento, OKR, KeyResult } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Building2, Users, Power, Loader2, FolderTree, Pencil, Trash2, ExternalLink, ShieldCheck, Target, FolderKanban, FileSpreadsheet, Shield, Network, BarChart3, List, Download, ChevronDown, FileText, Clock, TrendingUp, ClipboardList, User, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Navigate, useNavigate } from 'react-router-dom';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';
import { DadosEmpresaFields, emptyDadosEmpresa, type DadosEmpresaValues } from '@/components/empresa/DadosEmpresaFields';
import { maskCNPJ } from '@/lib/masks';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { estilizarCabecalho, autoWidth, zebra } from '@/lib/excelStyle';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Espelha backend/src/config/modules.ts (MODULE_KEYS) — usado só pra rotular
// modulos_habilitados/modulo aqui na tela, não é a fonte de verdade.
const MODULO_LABELS: Record<string, string> = {
  okrs: 'OKRs',
  projetos: 'Projetos',
  relatorios: 'Relatórios',
  auditoria: 'Auditoria',
  cronogramas: 'Cronogramas',
  organograma: 'Organograma',
  calendario: 'Calendário',
  fluxogramas: 'Fluxogramas',
  treinamentos: 'Treinamentos',
};

// Módulo antigo que o Projetos absorveu (2026-09-23): não tem mais interruptor nem etiqueta —
// o interruptor "Projetos" controla o menu. A chave pode continuar gravada em modulos_habilitados.
const MODULO_ABSORVIDO_POR_PROJETOS = 'cronogramas';

// Quantos eventos recentes de auditoria buscar pra derivar "última atividade" e
// "módulos com uso real" por empresa. Client-side, sem endpoint novo: os eventos já
// vêm ordenados por created_at DESC, então a primeira ocorrência de cada empresa_id
// já é a mais recente. Empresas sem nenhum evento nessa janela aparecem como sem atividade.
const AUDITORIA_RECENTE_LIMIT = 500;

const tipoLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  comum: 'Usuário',
};

export default function Empresas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [okrs, setOkrs] = useState<OKR[]>([]);
  const [keyResults, setKeyResults] = useState<KeyResult[]>([]);
  const [auditoriaRecentes, setAuditoriaRecentes] = useState<AuditoriaEventoEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [exportandoEmpresas, setExportandoEmpresas] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'created_desc' | 'created_asc' | 'name_asc' | 'name_desc'>('created_desc');
  const [statusFilter, setStatusFilter] = useState<'todas' | 'ativas' | 'inativas'>('todas');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Dialog states
  const [isEmpresaDialogOpen, setIsEmpresaDialogOpen] = useState(false);
  const [isEditEmpresaDialogOpen, setIsEditEmpresaDialogOpen] = useState(false);
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);

  // Form states
  const [empresaForm, setEmpresaForm] = useState({
    nome: '',
    modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] as string[],
  });
  const [empresaFormInitial, setEmpresaFormInitial] = useState({
    nome: '',
    modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] as string[],
  });
  const [dadosEmpresaForm, setDadosEmpresaForm] = useState<DadosEmpresaValues>(emptyDadosEmpresa);

  const [adminForm, setAdminForm] = useState({
    nome: '',
    email: '',
    senha: '',
  });
  const [adminFormInitial, setAdminFormInitial] = useState({
    nome: '',
    email: '',
    senha: '',
  });

  const [editUserForm, setEditUserForm] = useState({
    nome: '',
    email: '',
    tipo: 'comum' as 'admin' | 'comum',
    departamento_id: '',
  });
  const [editUserFormInitial, setEditUserFormInitial] = useState({
    nome: '',
    email: '',
    tipo: 'comum' as 'admin' | 'comum',
    departamento_id: '',
  });

  // Unsaved changes state
  const [showUnsavedEmpresa, setShowUnsavedEmpresa] = useState(false);
  const [showUnsavedAdmin, setShowUnsavedAdmin] = useState(false);
  const [showUnsavedEditUser, setShowUnsavedEditUser] = useState(false);

  const hasEmpresaChanges = useCallback(() => {
    return (
      JSON.stringify(empresaForm) !== JSON.stringify(empresaFormInitial) ||
      JSON.stringify(dadosEmpresaForm) !== JSON.stringify(emptyDadosEmpresa)
    );
  }, [empresaForm, empresaFormInitial, dadosEmpresaForm]);

  const hasAdminChanges = useCallback(() => {
    return JSON.stringify(adminForm) !== JSON.stringify(adminFormInitial);
  }, [adminForm, adminFormInitial]);

  const hasEditUserChanges = useCallback(() => {
    return JSON.stringify(editUserForm) !== JSON.stringify(editUserFormInitial);
  }, [editUserForm, editUserFormInitial]);

  const loadData = async () => {
    try {
      const [empresasRes, okrsRes, krsRes, auditoriaRes] = await Promise.all([
        empresaService.getAll(),
        okrService.getAll(),
        keyResultService.getAll(),
        auditoriaEventoService.getAll({ limit: AUDITORIA_RECENTE_LIMIT }),
      ]);
      if (empresasRes.success) setEmpresas(empresasRes.data);
      if (okrsRes.success) setOkrs(okrsRes.data);
      if (krsRes.success) setKeyResults(krsRes.data);
      if (auditoriaRes.success) setAuditoriaRecentes(auditoriaRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.tipo === 'superadmin') {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  // Filtro de status separado do de busca, pra poder mostrar a contagem de
  // "Ativas"/"Inativas" nas pílulas já considerando o texto pesquisado.
  const searchFilteredEmpresas = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return empresas.filter((empresa) => empresa.nome.toLowerCase().includes(normalizedSearch));
  }, [empresas, searchTerm]);

  const statusCounts = useMemo(() => ({
    todas: searchFilteredEmpresas.length,
    ativas: searchFilteredEmpresas.filter((e) => e.status === 'ativa').length,
    inativas: searchFilteredEmpresas.filter((e) => e.status !== 'ativa').length,
  }), [searchFilteredEmpresas]);

  const filteredAndSortedEmpresas = useMemo(() => {
    const filtered = statusFilter === 'todas'
      ? searchFilteredEmpresas
      : searchFilteredEmpresas.filter((e) => (statusFilter === 'ativas' ? e.status === 'ativa' : e.status !== 'ativa'));

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'name_asc') return a.nome.localeCompare(b.nome, 'pt-BR');
      if (sortBy === 'name_desc') return b.nome.localeCompare(a.nome, 'pt-BR');
      if (sortBy === 'created_asc') return new Date(a.data_criacao).getTime() - new Date(b.data_criacao).getTime();
      return new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime();
    });

    return sorted;
  }, [searchFilteredEmpresas, statusFilter, sortBy]);

  // Módulos habilitados x módulos com uso real recente + última atividade, por empresa.
  // Deriva de auditoriaRecentes (já ordenado created_at DESC) sem endpoint novo.
  const empresaModulosInfo = useMemo(() => {
    const info = new Map<string, {
      modulosComUso: string[];
      modulosOciosos: string[];
      ultimaAtividade: Date | null;
      ultimoModuloAtivo: string | null;
    }>();

    empresas.forEach(emp => {
      const eventosDaEmpresa = auditoriaRecentes.filter(ev => ev.empresa_id === emp.id);
      const ultimoEvento = eventosDaEmpresa[0];
      const modulosComUso = Array.from(new Set(eventosDaEmpresa.map(ev => ev.modulo)));
      const modulosHabilitados = emp.modulos_habilitados || [];
      info.set(emp.id, {
        modulosComUso,
        modulosOciosos: modulosHabilitados.filter(m => !modulosComUso.includes(m)),
        ultimaAtividade: ultimoEvento ? new Date(ultimoEvento.createdAt) : null,
        ultimoModuloAtivo: ultimoEvento ? (MODULO_LABELS[ultimoEvento.modulo] || ultimoEvento.modulo) : null,
      });
    });

    return info;
  }, [empresas, auditoriaRecentes]);

  // Dados dos 4 gráficos comparativos (por empresa) — migrado da antiga aba
  // "Comparativo" em Relatórios, já que é uma visão cross-empresa e essa página
  // (superadmin-only, lista todas as empresas) é o lugar certo pra isso viver.
  const chartDataByCompany = useMemo(() => {
    const okrsByCompany = empresas.map(emp => ({
      name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
      fullName: emp.nome,
      okrs: okrs.filter(o => o.empresa_id === emp.id).length,
    })).filter(d => d.okrs > 0);

    const progressByCompany = empresas.map(emp => {
      const companyOkrs = okrs.filter(o => o.empresa_id === emp.id);
      const avgProgress = companyOkrs.length > 0
        ? Math.round(companyOkrs.reduce((acc, o) => acc + o.progresso_geral, 0) / companyOkrs.length)
        : 0;
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        progresso: avgProgress,
      };
    }).filter(d => empresas.find(e => e.nome === d.fullName && okrs.some(o => o.empresa_id === e.id)));

    const statusByCompany = empresas.map(emp => {
      const companyOkrs = okrs.filter(o => o.empresa_id === emp.id);
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        'A Fazer': companyOkrs.filter(o => o.status === 'a_fazer').length,
        'Fazendo': companyOkrs.filter(o => o.status === 'fazendo').length,
        'Pausado': companyOkrs.filter(o => o.status === 'pausado').length,
        'Feito': companyOkrs.filter(o => o.status === 'feito').length,
        'Cancelado': companyOkrs.filter(o => o.status === 'cancelado').length,
      };
    }).filter(d => d['A Fazer'] + d['Fazendo'] + d['Pausado'] + d['Feito'] + d['Cancelado'] > 0);

    const krByCompany = empresas.map(emp => {
      const companyOkrIds = okrs.filter(o => o.empresa_id === emp.id).map(o => o.id);
      const companyKrs = keyResults.filter(kr => companyOkrIds.includes(kr.okr_id));
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        'Concluídos': companyKrs.filter(kr => kr.percentual === 100).length,
        'Em Progresso': companyKrs.filter(kr => kr.percentual > 0 && kr.percentual < 100).length,
        'Não Iniciados': companyKrs.filter(kr => kr.percentual === 0).length,
        total: companyKrs.length,
      };
    }).filter(d => d.total > 0);

    const peopleByCompany = empresas.map(emp => ({
      name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
      fullName: emp.nome,
      'Usuários': emp.usuario_count ?? 0,
      'Admins': emp.admin_count ?? 0,
    })).filter(d => d['Usuários'] > 0);

    const departmentsByCompany = empresas.map(emp => ({
      name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
      fullName: emp.nome,
      departamentos: emp.departamento_count ?? 0,
    })).filter(d => d.departamentos > 0);

    const modulosByCompany = empresas.map(emp => {
      const info = empresaModulosInfo.get(emp.id);
      return {
        name: emp.nome.length > 15 ? emp.nome.substring(0, 15) + '...' : emp.nome,
        fullName: emp.nome,
        'Em uso': info?.modulosComUso.length ?? 0,
        'Ociosos': info?.modulosOciosos.length ?? 0,
      };
    }).filter(d => d['Em uso'] + d['Ociosos'] > 0);

    return {
      okrsByCompany, progressByCompany, statusByCompany, krByCompany,
      peopleByCompany, departmentsByCompany, modulosByCompany,
    };
  }, [empresas, okrs, keyResults, empresaModulosInfo]);

  // KPIs de resumo no topo do comparativo — visão rápida antes de entrar nos gráficos.
  const comparativoResumo = useMemo(() => {
    const totalEmpresas = empresas.length;
    const totalUsuarios = empresas.reduce((acc, e) => acc + (e.usuario_count ?? 0), 0);
    const totalDepartamentos = empresas.reduce((acc, e) => acc + (e.departamento_count ?? 0), 0);
    const totalOkrs = okrs.length;
    const progressoMedioGeral = okrs.length > 0
      ? Math.round(okrs.reduce((acc, o) => acc + o.progresso_geral, 0) / okrs.length)
      : 0;
    return { totalEmpresas, totalUsuarios, totalDepartamentos, totalOkrs, progressoMedioGeral };
  }, [empresas, okrs]);

  // Linhas de export do comparativo — todas as empresas, mesmo com zero OKR/KR
  // (diferente dos arrays acima, que filtram pra não poluir os gráficos).
  const comparativoExportRows = useMemo(() => {
    return empresas.map(emp => {
      const companyOkrs = okrs.filter(o => o.empresa_id === emp.id);
      const companyOkrIds = companyOkrs.map(o => o.id);
      const companyKrs = keyResults.filter(kr => companyOkrIds.includes(kr.okr_id));
      const avgProgress = companyOkrs.length > 0
        ? Math.round(companyOkrs.reduce((acc, o) => acc + o.progresso_geral, 0) / companyOkrs.length)
        : 0;
      const info = empresaModulosInfo.get(emp.id);
      return {
        Empresa: emp.nome,
        Usuarios: emp.usuario_count ?? 0,
        Admins: emp.admin_count ?? 0,
        Departamentos: emp.departamento_count ?? 0,
        ModulosEmUso: info?.modulosComUso.length ?? 0,
        ModulosOciosos: info?.modulosOciosos.length ?? 0,
        TotalOKRs: companyOkrs.length,
        ProgressoMedio: `${avgProgress}%`,
        AFazer: companyOkrs.filter(o => o.status === 'a_fazer').length,
        Fazendo: companyOkrs.filter(o => o.status === 'fazendo').length,
        Pausado: companyOkrs.filter(o => o.status === 'pausado').length,
        Feito: companyOkrs.filter(o => o.status === 'feito').length,
        Cancelado: companyOkrs.filter(o => o.status === 'cancelado').length,
        TotalKR: companyKrs.length,
        KRConcluidos: companyKrs.filter(kr => kr.percentual === 100).length,
        KREmProgresso: companyKrs.filter(kr => kr.percentual > 0 && kr.percentual < 100).length,
        KRNaoIniciados: companyKrs.filter(kr => kr.percentual === 0).length,
      };
    });
  }, [empresas, okrs, keyResults, empresaModulosInfo]);

  const exportToXLSX = async (data: Record<string, string | number>[], filename: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
    const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    await saveOrShareBlob(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Arquivo ${filename}.xlsx exportado com sucesso`);
  };

  const exportToPDF = async (data: Record<string, string | number>[], filename: string, headers: string[], title: string) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    doc.setFontSize(10);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 22);
    const tableData = data.map(row => headers.map(h => String(row[h] ?? '')));
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 28,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] },
    });
    const blob = doc.output('blob');
    await saveOrShareBlob(blob, `${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success(`Arquivo ${filename}.pdf exportado com sucesso`);
  };

  // Export "Empresas" — aba Resumo Geral + 1 aba por empresa com as pessoas (aprovado com a
  // usuária em 26/08/2026, ver docs/proposta-relatorio-uso-2026-08-26.md). Busca usuários e
  // departamentos sob demanda (essas listas não são carregadas de cara nesta tela).
  const handleExportarEmpresas = async () => {
    setExportandoEmpresas(true);
    try {
      const [usuariosRes, departamentosRes] = await Promise.all([
        usuarioService.getAll(),
        departamentoService.getAll(),
      ]);
      const todosUsuarios = usuariosRes.success ? usuariosRes.data : [];
      const todosDepartamentos = departamentosRes.success ? departamentosRes.data : [];
      const nomeDepartamento = (id: string | null | undefined) =>
        todosDepartamentos.find((d) => d.id === id)?.nome ?? '—';

      const wb = new ExcelJS.Workbook();
      const resumo = wb.addWorksheet('Resumo Geral');
      resumo.columns = [
        { header: 'Empresa', key: 'empresa' },
        { header: 'Status', key: 'status' },
        { header: 'Total de Pessoas', key: 'totalPessoas' },
        { header: 'Admins', key: 'admins' },
        { header: 'Departamentos', key: 'departamentos' },
        { header: 'Módulos Habilitados', key: 'modulosHabilitados' },
        { header: 'Módulos em Uso', key: 'modulosEmUso' },
        { header: 'Módulos Ociosos', key: 'modulosOciosos' },
        { header: 'Última Atividade', key: 'ultimaAtividade' },
      ];
      empresas.forEach((emp) => {
        const info = empresaModulosInfo.get(emp.id);
        resumo.addRow({
          empresa: emp.nome,
          status: emp.status === 'ativa' ? 'Ativa' : 'Inativa',
          totalPessoas: emp.usuario_count ?? 0,
          admins: emp.admin_count ?? 0,
          departamentos: emp.departamento_count ?? 0,
          modulosHabilitados: (emp.modulos_habilitados || []).length,
          modulosEmUso: info?.modulosComUso.length ?? 0,
          modulosOciosos: info?.modulosOciosos.join(', ') || '—',
          ultimaAtividade: info?.ultimaAtividade ? info.ultimaAtividade.toLocaleDateString('pt-BR') : '—',
        });
      });
      estilizarCabecalho(resumo);
      ['totalPessoas', 'admins', 'departamentos', 'modulosHabilitados', 'modulosEmUso', 'status'].forEach((key) => {
        resumo.getColumn(key).alignment = { horizontal: 'center' };
      });
      resumo.eachRow((row, i) => {
        if (i === 1) return;
        const cell = row.getCell('status');
        const ativa = cell.value === 'Ativa';
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ativa ? 'FFC6EFCE' : 'FFFFC7CE' } };
        cell.font = { bold: true, color: { argb: ativa ? 'FF006100' : 'FF9C0006' } };
      });
      zebra(resumo);
      autoWidth(resumo, { modulosOciosos: 30, empresa: 18 });
      resumo.autoFilter = { from: 'A1', to: `${resumo.getColumn(resumo.columns.length).letter}1` };

      empresas.forEach((emp) => {
        const sheet = wb.addWorksheet(emp.nome.replace(/[[\]:*?/\\]/g, '').slice(0, 31) || 'Empresa');
        sheet.columns = [
          { header: 'Nome', key: 'nome' },
          { header: 'Departamento', key: 'departamento' },
          { header: 'Tipo de Acesso', key: 'tipo' },
          { header: 'Último Acesso', key: 'ultimoAcesso' },
        ];
        todosUsuarios
          // Vínculo-aware, igual ao `usuario_count` do backend (empresaController.ts): conta
          // empresa_id direto OU vínculo ativo em usuario_empresas — senão o total da aba
          // "Resumo Geral" diverge do número de linhas aqui pra empresas com usuário vinculado
          // só via usuario_empresas (revisão da PR #89, item 9).
          .filter((u) => u.empresa_id === emp.id || (u.usuarioEmpresas || []).some((ue) => ue.empresa_id === emp.id))
          .forEach((u) => {
            sheet.addRow({
              nome: u.nome,
              departamento: nomeDepartamento(u.departamento_id),
              tipo: u.tipo === 'admin' ? 'Admin' : u.tipo === 'superadmin' ? 'Super Admin' : 'Usuário',
              ultimoAcesso: u.ultimo_acesso ? new Date(u.ultimo_acesso as string).toLocaleDateString('pt-BR') : 'Nunca acessou',
            });
          });
        estilizarCabecalho(sheet);
        zebra(sheet);
        autoWidth(sheet);
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      await saveOrShareBlob(blob, `relatorio_empresas_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Exportação concluída');
    } catch (error) {
      console.error('Error exporting empresas:', error);
      toast.error('Erro ao exportar empresas');
    } finally {
      setExportandoEmpresas(false);
    }
  };

  const exportComparativoXLSX = () => exportToXLSX(comparativoExportRows, 'comparativo_empresas');
  const exportComparativoPDF = () => exportToPDF(
    comparativoExportRows,
    'comparativo_empresas',
    ['Empresa', 'TotalOKRs', 'ProgressoMedio', 'AFazer', 'Fazendo', 'Pausado', 'Feito', 'Cancelado', 'TotalKR', 'KRConcluidos', 'KREmProgresso', 'KRNaoIniciados'],
    'Comparativo entre Empresas'
  );

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedEmpresas.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedEmpresas = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedEmpresas.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedEmpresas, currentPage]);

  const handleCreateEmpresa = async () => {
    try {
      const result = await empresaService.create({
        nome: empresaForm.nome,
        status: 'ativa',
        data_criacao: new Date().toISOString().split('T')[0],
        modulos_habilitados: empresaForm.modulos_habilitados,
        ...dadosEmpresaForm,
      });

      if (result.success) {
        toast.success('Empresa criada com sucesso');
        loadData();
        setIsEmpresaDialogOpen(false);
        setEmpresaForm({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
        setEmpresaFormInitial({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
        setDadosEmpresaForm(emptyDadosEmpresa);
        // Redirecionar para a página de gerenciamento da nova empresa
        navigate(`/empresas/${result.data.id}`);
      } else {
        toast.error(result.error || 'Erro ao criar empresa');
      }
    } catch (error) {
      toast.error('Erro ao criar empresa');
    }
  };

  const handleUpdateEmpresa = async () => {
    if (!editingEmpresa) return;

    try {
      const result = await empresaService.update(editingEmpresa.id, {
        nome: empresaForm.nome,
      });

      if (result.success) {
        toast.success('Empresa atualizada com sucesso');
        loadData();
        setIsEditEmpresaDialogOpen(false);
        setEditingEmpresa(null);
        setEmpresaForm({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
        setEmpresaFormInitial({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
      } else {
        toast.error(result.error || 'Erro ao atualizar empresa');
      }
    } catch (error) {
      toast.error('Erro ao atualizar empresa');
    }
  };

  const openEditEmpresa = (empresa: Empresa) => {
    setEditingEmpresa(empresa);
    const initial = { nome: empresa.nome, modulos_habilitados: empresa.modulos_habilitados || [] };
    setEmpresaForm(initial);
    setEmpresaFormInitial(initial);
    setIsEditEmpresaDialogOpen(true);
  };

  const handleCloseEditEmpresaDialog = useCallback(() => {
    if (hasEmpresaChanges()) {
      setShowUnsavedEmpresa(true);
    } else {
      setIsEditEmpresaDialogOpen(false);
      setEditingEmpresa(null);
      setEmpresaForm({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
      setEmpresaFormInitial({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
    }
  }, [hasEmpresaChanges]);

  const handleCreateAdmin = async () => {
    if (!selectedEmpresa) return;

    try {
      const result = await usuarioService.create({
        nome: adminForm.nome,
        email: adminForm.email,
        senha: adminForm.senha,
        tipo: 'admin',
        empresa_id: selectedEmpresa.id,
      });

      if (result.success) {
        toast.success('Administrador criado com sucesso');
        loadData();
        setIsAdminDialogOpen(false);
        setAdminForm({ nome: '', email: '', senha: '' });
        setAdminFormInitial({ nome: '', email: '', senha: '' });
        setSelectedEmpresa(null);
      }
    } catch (error) {
      toast.error('Erro ao criar administrador');
    }
  };

  const openNewEmpresa = () => {
    const initial = { nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] };
    setEmpresaForm(initial);
    setEmpresaFormInitial(initial);
    setDadosEmpresaForm(emptyDadosEmpresa);
    setIsEmpresaDialogOpen(true);
  };

  const handleCloseEmpresaDialog = useCallback(() => {
    if (hasEmpresaChanges()) {
      setShowUnsavedEmpresa(true);
    } else {
      setIsEmpresaDialogOpen(false);
      setEmpresaForm({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
      setEmpresaFormInitial({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
      setDadosEmpresaForm(emptyDadosEmpresa);
    }
  }, [hasEmpresaChanges]);

  const openNewAdmin = (empresa: Empresa) => {
    setSelectedEmpresa(empresa);
    const initial = { nome: '', email: '', senha: '' };
    setAdminForm(initial);
    setAdminFormInitial(initial);
    setIsAdminDialogOpen(true);
  };

  const handleCloseAdminDialog = useCallback(() => {
    if (hasAdminChanges()) {
      setShowUnsavedAdmin(true);
    } else {
      setIsAdminDialogOpen(false);
      setSelectedEmpresa(null);
      setAdminForm({ nome: '', email: '', senha: '' });
      setAdminFormInitial({ nome: '', email: '', senha: '' });
    }
  }, [hasAdminChanges]);

  const openDetails = (empresa: Empresa) => {
    setSelectedEmpresa(empresa);
    setIsDetailsDialogOpen(true);
  };

  const openEditUser = (usuario: Usuario) => {
    setSelectedUser(usuario);
    const initial = {
      nome: usuario.nome,
      email: usuario.email,
      tipo: usuario.tipo as 'admin' | 'comum',
      departamento_id: usuario.departamento_id || '',
    };
    setEditUserForm(initial);
    setEditUserFormInitial(initial);
    setIsEditUserDialogOpen(true);
  };

  const handleCloseEditUserDialog = useCallback(() => {
    if (hasEditUserChanges()) {
      setShowUnsavedEditUser(true);
    } else {
      setIsEditUserDialogOpen(false);
      setSelectedUser(null);
      setEditUserForm({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
      setEditUserFormInitial({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
    }
  }, [hasEditUserChanges]);

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const result = await usuarioService.update(selectedUser.id, {
        nome: editUserForm.nome,
        email: editUserForm.email,
        tipo: editUserForm.tipo,
        departamento_id: editUserForm.departamento_id || null,
      });

      if (result.success) {
        toast.success('Usuário atualizado com sucesso');
        loadData();
        setIsEditUserDialogOpen(false);
        setSelectedUser(null);
        setEditUserForm({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
        setEditUserFormInitial({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
      }
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const openDeleteUser = (usuario: Usuario) => {
    setSelectedUser(usuario);
    setIsDeleteUserDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const result = await usuarioService.delete(selectedUser.id);

      if (result.success) {
        toast.success('Usuário excluído com sucesso');
        loadData();
        setIsDeleteUserDialogOpen(false);
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const getUsersForEmpresa = (empresaId: string) =>
    usuarios.filter((u) => u.empresa_id === empresaId);

  const getDepartamentosForEmpresa = (empresaId: string) =>
    departamentos.filter((d) => d.empresa_id === empresaId);

  // Only super admin can access this page
  if (user?.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Empresas</h1>
            <p className="text-muted-foreground">
              Gerencie as empresas do sistema
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportarEmpresas} disabled={exportandoEmpresas}>
              {exportandoEmpresas ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Exportar Empresas
            </Button>
            <Button onClick={openNewEmpresa}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Empresa
            </Button>
          </div>
        </div>

        <Tabs defaultValue="lista" className="space-y-4">
          <TabsList>
            <TabsTrigger value="lista" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              Lista
            </TabsTrigger>
            <TabsTrigger value="comparativo" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Comparativo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lista">
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Pesquisar por nome da empresa"
                  className="sm:max-w-xs"
                />
                <div className="flex items-center gap-1 rounded-md border bg-muted/40 p-1">
                  {([
                    { value: 'todas', label: 'Todas' },
                    { value: 'ativas', label: 'Ativas' },
                    { value: 'inativas', label: 'Inativas' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatusFilter(opt.value)}
                      className={cn(
                        'rounded px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap',
                        statusFilter === opt.value
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {opt.label} · {statusCounts[opt.value]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="sort-empresas" className="text-sm text-muted-foreground whitespace-nowrap">
                  Ordenar por
                </Label>
                <Select value={sortBy} onValueChange={(value: 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc') => setSortBy(value)}>
                  <SelectTrigger id="sort-empresas" className="w-[240px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_desc">Data de criação (mais recente)</SelectItem>
                    <SelectItem value="created_asc">Data de criação (mais antiga)</SelectItem>
                    <SelectItem value="name_asc">Ordem alfabética (A-Z)</SelectItem>
                    <SelectItem value="name_desc">Ordem alfabética (Z-A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block">
              <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="py-2 text-center min-w-[280px]">Empresa</TableHead>
                    <TableHead className="py-2 text-center">Pessoas</TableHead>
                    <TableHead className="py-2 text-center min-w-[240px]">Módulos habilitados</TableHead>
                    <TableHead className="py-2 text-center min-w-[200px]">Última atividade</TableHead>
                    <TableHead className="py-2 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEmpresas.map((empresa) => {
                    const userCount = empresa.usuario_count ?? 0;
                    const deptCount = empresa.departamento_count ?? 0;
                    const adminCount = empresa.admin_count ?? 0;
                    const modInfo = empresaModulosInfo.get(empresa.id);

                    return (
                      <TableRow
                        key={empresa.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/empresas/${empresa.id}`)}
                      >
                        <TableCell className="py-2.5">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div>
                              <div className="font-medium">{empresa.nome}</div>
                              {empresa.cnpj && (
                                <div className="text-xs text-muted-foreground">
                                  {maskCNPJ(empresa.cnpj)}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5 text-sm text-muted-foreground whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />{userCount} usuário{userCount !== 1 ? 's' : ''}
                          </span>
                          <span className="mx-1.5 text-muted-foreground/50">·</span>
                          <span className="inline-flex items-center gap-1">
                            <FolderTree className="h-3.5 w-3.5" />{deptCount} depto{deptCount !== 1 ? 's' : ''}
                          </span>
                          <span className="mx-1.5 text-muted-foreground/50">·</span>
                          <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {adminCount > 0 ? `${adminCount} admin${adminCount !== 1 ? 's' : ''}` : 'sem admin'}
                          </span>
                        </TableCell>
                        <TableCell className="py-2.5">
                          <div className="flex flex-wrap gap-1">
                            {(empresa.modulos_habilitados || []).filter(m => m !== MODULO_ABSORVIDO_POR_PROJETOS).map(m => (
                              <Badge
                                key={m}
                                variant={modInfo?.modulosOciosos.includes(m) ? 'secondary' : 'outline'}
                                className={cn('text-xs', modInfo?.modulosOciosos.includes(m) && 'text-amber-700 dark:text-amber-400')}
                                title={modInfo?.modulosOciosos.includes(m) ? 'Habilitado, sem uso registrado recentemente' : undefined}
                              >
                                {MODULO_LABELS[m] || m}
                              </Badge>
                            ))}
                            {(empresa.modulos_habilitados || []).length === 0 && (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5">
                          {modInfo?.ultimaAtividade ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span>
                                {modInfo.ultimaAtividade.toLocaleString('pt-BR')}
                                <span className="text-muted-foreground"> · {modInfo.ultimoModuloAtivo}</span>
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Sem atividade recente</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2.5 text-center">
                          <Badge
                            variant={empresa.status === 'ativa' ? 'default' : 'secondary'}
                            className={cn(
                              empresa.status === 'ativa'
                                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                                : 'bg-gray-100 text-gray-600'
                            )}
                          >
                            {empresa.status === 'ativa' ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {filteredAndSortedEmpresas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhuma empresa encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3 p-4">
              {paginatedEmpresas.map((empresa) => {
                const userCount = empresa.usuario_count ?? 0;
                const deptCount = empresa.departamento_count ?? 0;
                const adminCount = empresa.admin_count ?? 0;
                const modInfo = empresaModulosInfo.get(empresa.id);

                return (
                  <Card
                    key={empresa.id}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/empresas/${empresa.id}`)}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <span className="font-semibold truncate block">{empresa.nome}</span>
                            {empresa.cnpj && (
                              <span className="text-xs text-muted-foreground block">
                                {maskCNPJ(empresa.cnpj)}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge
                          variant={empresa.status === 'ativa' ? 'default' : 'secondary'}
                          className={cn(
                            'text-xs shrink-0',
                            empresa.status === 'ativa'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          {empresa.status === 'ativa' ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Users className="h-4 w-4" />
                          <span>{userCount} usuário{userCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <FolderTree className="h-4 w-4" />
                          <span>{deptCount} depto{deptCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {new Date(empresa.data_criacao).toLocaleDateString('pt-BR')}
                        </div>
                      </div>

                      {adminCount > 0 && (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <ShieldCheck className="h-4 w-4" />
                          <span>{adminCount} admin{adminCount !== 1 ? 's' : ''}</span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1">
                        {(empresa.modulos_habilitados || []).filter(m => m !== MODULO_ABSORVIDO_POR_PROJETOS).map(m => (
                          <Badge
                            key={m}
                            variant={modInfo?.modulosOciosos.includes(m) ? 'secondary' : 'outline'}
                            className={cn('text-xs', modInfo?.modulosOciosos.includes(m) && 'text-amber-700 dark:text-amber-400')}
                          >
                            {MODULO_LABELS[m] || m}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {modInfo?.ultimaAtividade
                            ? `${modInfo.ultimaAtividade.toLocaleString('pt-BR')} · ${modInfo.ultimoModuloAtivo}`
                            : 'Sem atividade recente'}
                        </span>
                      </div>

                    </CardContent>
                  </Card>
                );
              })}

              {filteredAndSortedEmpresas.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Building2 className="h-12 w-12 mb-3 opacity-50" />
                  <p className="text-sm">Nenhuma empresa encontrada</p>
                </div>
              )}
            </div>

            {filteredAndSortedEmpresas.length > 0 && (
              <div className="border-t p-4 flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Página {currentPage} de {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="comparativo">
            <div className="grid gap-4">
              <div className="flex flex-row items-center justify-between">
                <h2 className="text-lg font-semibold">Comparativo entre Empresas</h2>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="gap-2">
                      <Download className="h-4 w-4" />
                      Exportar
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportComparativoXLSX} className="gap-2">
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel (XLSX)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportComparativoPDF} className="gap-2">
                      <FileText className="h-4 w-4" />
                      PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* KPIs de resumo */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-xl font-bold leading-none">{comparativoResumo.totalEmpresas}</div>
                      <div className="text-xs text-muted-foreground">empresas</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-xl font-bold leading-none">{comparativoResumo.totalUsuarios}</div>
                      <div className="text-xs text-muted-foreground">usuários</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <FolderTree className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-xl font-bold leading-none">{comparativoResumo.totalDepartamentos}</div>
                      <div className="text-xs text-muted-foreground">departamentos</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <Target className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-xl font-bold leading-none">{comparativoResumo.totalOkrs}</div>
                      <div className="text-xs text-muted-foreground">OKRs</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex items-center gap-3 p-4">
                    <TrendingUp className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-xl font-bold leading-none">{comparativoResumo.progressoMedioGeral}%</div>
                      <div className="text-xs text-muted-foreground">progresso médio</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Gráficos — grid de dashboards menores */}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      OKRs por Empresa
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.okrsByCompany} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" className="text-xs" />
                          <YAxis dataKey="name" type="category" width={90} className="text-xs" />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            formatter={(value) => [value, 'OKRs']}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="okrs" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      Progresso Médio
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.progressByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis domain={[0, 100]} className="text-xs" />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            formatter={(value) => [`${value}%`, 'Progresso Médio']}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="progresso" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Pessoas por Empresa
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.peopleByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Usuários" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Admins" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <FolderTree className="h-4 w-4 text-muted-foreground" />
                      Departamentos por Empresa
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.departmentsByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            formatter={(value) => [value, 'Departamentos']}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Bar dataKey="departamentos" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                      Módulos: em uso x ociosos
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.modulosByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Em uso" stackId="m" fill="#10b981" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="Ociosos" stackId="m" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      Key Results por Empresa
                    </h3>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.krByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="Concluídos" fill="#10b981" />
                          <Bar dataKey="Em Progresso" fill="#3b82f6" />
                          <Bar dataKey="Não Iniciados" fill="#94a3b8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2 xl:col-span-3">
                  <CardContent className="pt-4">
                    <h3 className="flex items-center gap-1.5 text-sm font-medium mb-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      Status de OKRs por Empresa
                    </h3>
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataByCompany.statusByCompany}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="A Fazer" stackId="a" fill="#f59e0b" />
                          <Bar dataKey="Fazendo" stackId="a" fill="#3b82f6" />
                          <Bar dataKey="Feito" stackId="a" fill="#10b981" />
                          <Bar dataKey="Cancelado" stackId="a" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedEmpresa?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmpresa && (
            <Tabs defaultValue="usuarios" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="usuarios" className="gap-2">
                  <Users className="h-4 w-4" />
                  Usuários ({getUsersForEmpresa(selectedEmpresa.id).length})
                </TabsTrigger>
                <TabsTrigger value="departamentos" className="gap-2">
                  <FolderTree className="h-4 w-4" />
                  Departamentos ({getDepartamentosForEmpresa(selectedEmpresa.id).length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="usuarios" className="flex-1 overflow-auto mt-4">
                {getUsersForEmpresa(selectedEmpresa.id).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mb-4" />
                    <p>Nenhum usuário cadastrado</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getUsersForEmpresa(selectedEmpresa.id).map((usuario) => (
                      <div
                        key={usuario.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{usuario.nome}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {usuario.email}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={cn(
                              usuario.tipo === 'admin' && 'bg-blue-100 text-blue-800 border-blue-200',
                              usuario.tipo === 'comum' && 'bg-gray-100 text-gray-800 border-gray-200'
                            )}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {tipoLabels[usuario.tipo] || usuario.tipo}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditUser(usuario)}
                            title="Editar usuário"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => openDeleteUser(usuario)}
                            title="Excluir usuário"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="departamentos" className="flex-1 overflow-auto mt-4">
                {getDepartamentosForEmpresa(selectedEmpresa.id).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <FolderTree className="h-12 w-12 mb-4" />
                    <p>Nenhum departamento cadastrado</p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    {getDepartamentosForEmpresa(selectedEmpresa.id).map((dept) => {
                      const deptUsers = usuarios.filter(u => u.departamento_id === dept.id);
                      return (
                        <div
                          key={dept.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <FolderTree className="h-5 w-5 text-primary" />
                            </div>
                            <p className="font-medium">{dept.nome}</p>
                          </div>
                          <Badge variant="secondary" className="gap-1">
                            <Users className="h-3 w-3" />
                            {deptUsers.length} usuário{deptUsers.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Empresa Dialog */}
      <Dialog open={isEmpresaDialogOpen} onOpenChange={(open) => { if (!open) handleCloseEmpresaDialog(); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="empresa_nome">Nome da Empresa</Label>
              <Input
                id="empresa_nome"
                value={empresaForm.nome}
                onChange={(e) => setEmpresaForm({ ...empresaForm, nome: e.target.value })}
                placeholder="Ex: Tech Solutions Ltda"
              />
            </div>
            <div className="space-y-2">
              <Label>Módulos Habilitados</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'okrs', label: 'OKRs', desc: 'OKRs e Kanban', Icon: Target },
                  { key: 'projetos', label: 'Projetos', desc: 'Gestão de projetos', Icon: FolderKanban },
                  { key: 'relatorios', label: 'Relatórios', desc: 'Relatórios', Icon: FileSpreadsheet },
                  { key: 'auditoria', label: 'Auditoria', desc: 'Log de auditoria', Icon: ShieldCheck },
                  { key: 'organograma', label: 'Organograma', desc: 'Estrutura da empresa', Icon: Network },
                  { key: 'gestao-atividades', label: 'Formulários', desc: 'Formulários e checklists', Icon: ClipboardList },
                ].map(({ key, label, desc, Icon }) => (
                  <div
                    key={key}
                    className={cn(
                      'flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-all',
                      empresaForm.modulos_habilitados.includes(key)
                        ? 'border-primary bg-primary/5'
                        : 'border-border'
                    )}
                    onClick={() => {
                      const mods = empresaForm.modulos_habilitados.includes(key)
                        ? empresaForm.modulos_habilitados.filter(m => m !== key)
                        : [...empresaForm.modulos_habilitados, key];
                      setEmpresaForm({ ...empresaForm, modulos_habilitados: mods });
                    }}
                  >
                    <Checkbox
                      checked={empresaForm.modulos_habilitados.includes(key)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium text-xs">{label}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3 border-t pt-4">
              <div>
                <Label className="text-sm font-medium">Dados da empresa (opcional)</Label>
                <p className="text-xs text-muted-foreground">
                  Informe CNPJ, razão social e endereço. Ao preencher o CEP os campos são completados automaticamente.
                </p>
              </div>
              <DadosEmpresaFields
                values={dadosEmpresaForm}
                onChange={(patch) => setDadosEmpresaForm((prev) => ({ ...prev, ...patch }))}
                idPrefix="nova-empresa"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEmpresaDialog}>
              Cancelar
            </Button>
            <Button onClick={handleCreateEmpresa} disabled={!empresaForm.nome}>
              Criar Empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Empresa Dialog */}
      <Dialog open={isEditEmpresaDialogOpen} onOpenChange={(open) => { if (!open) handleCloseEditEmpresaDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_empresa_nome">Nome da Empresa</Label>
              <Input
                id="edit_empresa_nome"
                value={empresaForm.nome}
                onChange={(e) => setEmpresaForm({ ...empresaForm, nome: e.target.value })}
                placeholder="Ex: Tech Solutions Ltda"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditEmpresaDialog}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateEmpresa} disabled={!empresaForm.nome}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Empresa Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedEmpresa}
        onOpenChange={setShowUnsavedEmpresa}
        onConfirm={() => {
          setShowUnsavedEmpresa(false);
          if (isEmpresaDialogOpen) {
            setIsEmpresaDialogOpen(false);
          }
          if (isEditEmpresaDialogOpen) {
            setIsEditEmpresaDialogOpen(false);
            setEditingEmpresa(null);
          }
          setEmpresaForm({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
          setEmpresaFormInitial({ nome: '', modulos_habilitados: ['okrs', 'projetos', 'relatorios', 'auditoria', 'cronogramas', 'organograma'] });
          setDadosEmpresaForm(emptyDadosEmpresa);
        }}
        onCancel={() => setShowUnsavedEmpresa(false)}
      />

      {/* Create Admin Dialog */}
      <Dialog
        open={isAdminDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseAdminDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Administrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground">
                Empresa: <strong className="text-foreground">{selectedEmpresa?.nome}</strong>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_nome">Nome</Label>
              <Input
                id="admin_nome"
                value={adminForm.nome}
                onChange={(e) => setAdminForm({ ...adminForm, nome: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_email">Email</Label>
              <Input
                id="admin_email"
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="email@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_senha">Senha</Label>
              <Input
                id="admin_senha"
                type="password"
                value={adminForm.senha}
                onChange={(e) => setAdminForm({ ...adminForm, senha: e.target.value })}
                placeholder="••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseAdminDialog}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAdmin}
              disabled={!adminForm.nome || !adminForm.email || !adminForm.senha}
            >
              Criar Administrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Admin Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedAdmin}
        onOpenChange={setShowUnsavedAdmin}
        onConfirm={() => {
          setShowUnsavedAdmin(false);
          setIsAdminDialogOpen(false);
          setSelectedEmpresa(null);
          setAdminForm({ nome: '', email: '', senha: '' });
          setAdminFormInitial({ nome: '', email: '', senha: '' });
        }}
        onCancel={() => setShowUnsavedAdmin(false)}
      />

      {/* Edit User Dialog */}
      <Dialog
        open={isEditUserDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseEditUserDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_user_nome">Nome</Label>
              <Input
                id="edit_user_nome"
                value={editUserForm.nome}
                onChange={(e) => setEditUserForm({ ...editUserForm, nome: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_user_email">Email</Label>
              <Input
                id="edit_user_email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                placeholder="email@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_user_tipo">Tipo</Label>
              <Select
                value={editUserForm.tipo}
                onValueChange={(value) => setEditUserForm({ ...editUserForm, tipo: value as 'admin' | 'comum' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="comum">Usuário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedEmpresa && (
              <div className="space-y-2">
                <Label htmlFor="edit_user_departamento">Departamento</Label>
                <Select
                  value={editUserForm.departamento_id || "none"}
                  onValueChange={(value) => setEditUserForm({ ...editUserForm, departamento_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {getDepartamentosForEmpresa(selectedEmpresa.id).map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditUserDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateUser}
              disabled={!editUserForm.nome || !editUserForm.email}
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Edit User Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedEditUser}
        onOpenChange={setShowUnsavedEditUser}
        onConfirm={() => {
          setShowUnsavedEditUser(false);
          setIsEditUserDialogOpen(false);
          setSelectedUser(null);
          setEditUserForm({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
          setEditUserFormInitial({ nome: '', email: '', tipo: 'comum', departamento_id: '' });
        }}
        onCancel={() => setShowUnsavedEditUser(false)}
      />

      {/* Delete User Confirmation */}
      <AlertDialog open={isDeleteUserDialogOpen} onOpenChange={setIsDeleteUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário <strong>{selectedUser?.nome}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
